import type { AwakeningAnswers, Locale } from '@athrelaris/shared';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import i18next from 'i18next';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandMark } from '../components/BrandMark';
import { type AppLocale, detectBrowserLocale, switchLocaleToBond } from '../i18n/index.js';
import { completeAwakening, getMyBond } from '../lib/api';
import { authClient, getSessionSafe } from '../lib/auth-client';
import { redirectIfLandingOnly } from '../lib/landing-mode';

export const Route = createFileRoute('/awakening')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) {
      throw redirect({ to: '/login' });
    }
    const bond = await getMyBond();
    if (bond) {
      throw redirect({ to: '/home' });
    }
  },
  component: AwakeningPage,
});

type Phase = 'locale' | 'opening' | 'step' | 'completing' | 'error';
type Answers = Partial<AwakeningAnswers>;

const STEP_KEYS = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'] as const;
type StepKey = (typeof STEP_KEYS)[number];

// Each ChoiceQuestion is (mostly) i18n-driven now: the displayed
// title/prompt/option labels read from `awakening.questions.{step}.*`.
// The structural data — which option keys exist for each step — stays
// on this side because the API expects those exact codes.
const QUESTION_OPTIONS: Record<StepKey, string[]> = {
  step1: ['warmth_left_behind', 'weight_unsaid', 'movement_unseen', 'shape_remaining'],
  step2: ['lantern_in_ruin', 'lake_of_lost_sky', 'bridge_between_skies', 'root_gate_under_stone'],
  step3: ['urgency', 'sorrow', 'wonder', 'caution'],
  step4: ['cracked_mask', 'silent_bell', 'warm_glass_shard', 'breathing_seed', 'burned_ribbon'],
  step5: ['made_to_destroy', 'forgot_someone', 'voice_not_own', 'remembering_buries'],
  step6: ['protect_unnamed_light', 'hear_almost_song', 'find_broken_sky', 'return_under_stone'],
};

const NUMERALS: Record<StepKey, string> = {
  step1: 'I',
  step2: 'II',
  step3: 'III',
  step4: 'IV',
  step5: 'V',
  step6: 'VI',
};

const TOTAL_STEPS = 7;

function AwakeningPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Locale pick lives at phase='locale'. Browser detection seeds the
  // initial choice, then the user has to confirm with a click — the
  // Bond's locale is locked for life so consent matters more than
  // silent auto-route.
  const [phase, setPhase] = useState<Phase>('locale');
  const [locale, setLocale] = useState<AppLocale>(() => detectBrowserLocale());
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [firstWords, setFirstWords] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleLocalePick(picked: AppLocale) {
    setLocale(picked);
    switchLocaleToBond(picked);
    setPhase('opening');
  }

  function handleChoice(field: StepKey, value: string) {
    setAnswers((prev) => ({ ...prev, [field]: value }));
    if (stepIndex < STEP_KEYS.length) {
      setStepIndex((i) => i + 1);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (firstWords.trim().length === 0) return;
    setPhase('completing');
    setError(null);
    try {
      const finalAnswers: AwakeningAnswers = {
        ...(answers as Required<Omit<AwakeningAnswers, 'step7' | 'locale'>>),
        step7: firstWords.trim(),
        locale: locale as Locale,
      };
      await completeAwakening(finalAnswers);
      await navigate({ to: '/home' });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('awakening.errorScreen.defaultMessage'),
      );
      setPhase('error');
    }
  }

  if (phase === 'locale') {
    return <LocalePick initialLocale={locale} onPick={handleLocalePick} />;
  }

  if (phase === 'opening') {
    return <Opening onBegin={() => setPhase('step')} />;
  }

  if (phase === 'completing') {
    return <CompletingScreen />;
  }

  if (phase === 'error') {
    return (
      <ErrorScreen
        message={error ?? t('awakening.errorScreen.heading')}
        onRetry={() => {
          setError(null);
          setPhase('step');
        }}
      />
    );
  }

  const currentKey = STEP_KEYS[stepIndex];
  if (currentKey) {
    return (
      <ChoiceStep
        key={currentKey}
        stepKey={currentKey}
        progress={stepIndex / TOTAL_STEPS}
        onSelect={(value) => handleChoice(currentKey, value)}
      />
    );
  }

  return (
    <FirstWordsStep
      progress={6 / TOTAL_STEPS}
      value={firstWords}
      onChange={setFirstWords}
      onSubmit={handleSubmit}
    />
  );
}

function LocalePick({
  initialLocale,
  onPick,
}: {
  initialLocale: AppLocale;
  onPick: (l: AppLocale) => void;
}) {
  // The Step 0 surface temporarily switches the i18n language to the
  // currently-hovered/selected option so the heading + body re-render
  // in that language as a live preview. Confirms the choice once the
  // user clicks.
  const [previewLocale, setPreviewLocale] = useState<AppLocale>(initialLocale);
  // Force the i18n language to the preview while this surface lives.
  if (i18next.language !== previewLocale) {
    void i18next.changeLanguage(previewLocale);
  }
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
      <BrandMark />
      <p className="mt-10 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.5em] sm:text-xs">
        {t('common.awakeningStep0.eyebrow')}
      </p>
      <h1 className="mt-5 max-w-2xl font-display font-light text-ash-100 text-2xl leading-snug sm:text-3xl">
        {t('common.awakeningStep0.heading')}
      </h1>
      <p className="mt-5 max-w-md font-body text-ash-300 italic leading-relaxed sm:text-lg">
        {t('common.awakeningStep0.body')}
      </p>

      <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:gap-4">
        <button
          type="button"
          onMouseEnter={() => setPreviewLocale('en')}
          onFocus={() => setPreviewLocale('en')}
          onClick={() => onPick('en')}
          className={`border px-12 py-4 font-display text-sm uppercase tracking-[0.4em] transition ${
            previewLocale === 'en'
              ? 'border-moonlit-400 text-ash-100'
              : 'border-ash-700 text-ash-300 hover:border-moonlit-400 hover:text-ash-100'
          }`}
        >
          {t('common.awakeningStep0.english')}
        </button>
        <button
          type="button"
          onMouseEnter={() => setPreviewLocale('es')}
          onFocus={() => setPreviewLocale('es')}
          onClick={() => onPick('es')}
          className={`border px-12 py-4 font-display text-sm uppercase tracking-[0.4em] transition ${
            previewLocale === 'es'
              ? 'border-moonlit-400 text-ash-100'
              : 'border-ash-700 text-ash-300 hover:border-moonlit-400 hover:text-ash-100'
          }`}
        >
          {t('common.awakeningStep0.spanish')}
        </button>
      </div>

      <p className="mt-8 font-body text-ash-500 text-xs italic">
        {t('common.awakeningStep0.hint')}
      </p>

      <div className="hairline mt-16 h-px w-32" />
    </div>
  );
}

function Opening({ onBegin }: { onBegin: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
      <BrandMark />
      <div className="mt-12 max-w-xl space-y-5 font-body text-ash-200 sm:text-lg">
        <p>{t('awakening.opening.lineA')}</p>
        <p>
          {t('awakening.opening.lineB1')}
          <br />
          {t('awakening.opening.lineB2')}
          <br />
          {t('awakening.opening.lineB3')}
        </p>
        <p className="italic text-ash-300">{t('awakening.opening.lineC')}</p>
      </div>
      <button
        type="button"
        onClick={onBegin}
        className="mt-14 border border-ash-700 px-10 py-3 font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:border-moonlit-400 hover:text-ash-100"
      >
        {t('awakening.opening.begin')}
      </button>
      <div className="hairline mt-16 h-px w-32" />
    </div>
  );
}

function ChoiceStep({
  stepKey,
  progress,
  onSelect,
}: {
  stepKey: StepKey;
  progress: number;
  onSelect: (value: string) => void;
}) {
  const { t } = useTranslation();
  const numeral = NUMERALS[stepKey];
  const title = t(`awakening.questions.${stepKey}.title`);
  const prompt = t(`awakening.questions.${stepKey}.prompt`);
  const optionKeys = QUESTION_OPTIONS[stepKey];
  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
      <StepIndicator numeral={numeral} title={title} progress={progress} />
      <h1 className="mt-10 max-w-2xl font-display font-light text-ash-100 text-2xl leading-snug sm:text-3xl">
        {prompt}
      </h1>
      <div className="mt-12 flex w-full max-w-xl flex-col gap-3">
        {optionKeys.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onSelect(value)}
            className="border border-ash-700 px-6 py-4 text-left font-body text-ash-200 leading-relaxed transition hover:border-moonlit-400 hover:bg-paper-800/40 hover:text-ash-100 sm:px-8 sm:text-lg"
          >
            {t(`awakening.questions.${stepKey}.options.${value}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

function FirstWordsStep({
  progress,
  value,
  onChange,
  onSubmit,
}: {
  progress: number;
  value: string;
  onChange: (s: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
      <StepIndicator
        numeral="VII"
        title={t('awakening.firstWords.title')}
        progress={progress}
      />
      <div className="mt-10 max-w-xl space-y-3">
        <p className="font-body text-ash-300 italic sm:text-lg">
          {t('awakening.firstWords.eyebrow')}
        </p>
        <h1 className="font-display font-light text-ash-100 text-2xl leading-snug sm:text-3xl">
          {t('awakening.firstWords.heading')}
        </h1>
      </div>
      <form onSubmit={onSubmit} className="mt-12 w-full max-w-xl">
        <label htmlFor="first-words" className="sr-only">
          {t('awakening.firstWords.ariaLabel')}
        </label>
        <textarea
          id="first-words"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          maxLength={500}
          rows={5}
          placeholder={t('awakening.firstWords.placeholder')}
          className="w-full resize-none border border-ash-700 bg-transparent p-5 text-center font-body text-ash-100 text-lg leading-relaxed placeholder:text-ash-500 focus:border-moonlit-400 focus:outline-none"
        />
        <button
          type="submit"
          disabled={value.trim().length === 0}
          className="mt-8 border border-ash-700 px-10 py-3 font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:border-moonlit-400 hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t('awakening.firstWords.submit')}
        </button>
      </form>
    </div>
  );
}

function StepIndicator({
  numeral,
  title,
  progress,
}: {
  numeral: string;
  title: string;
  progress: number;
}) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center gap-3 font-display text-ash-400 text-xs uppercase tracking-[0.4em]">
        <span>{numeral}</span>
        <span className="h-px w-8 bg-ash-700" />
        <span>{title}</span>
      </div>
      <div className="h-px w-48 bg-ash-700/50">
        <div
          className="h-full bg-moonlit-400 transition-all duration-700"
          style={{ width: `${Math.max(progress * 100, 6)}%` }}
        />
      </div>
    </div>
  );
}

function CompletingScreen() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
      <div className="portrait-glow size-32 rounded-full opacity-70 animate-softGlow" />
      <p className="mt-10 font-display font-light text-ash-200 text-2xl italic sm:text-3xl">
        {t('awakening.completing.heading')}
      </p>
      <p className="mt-4 font-body text-ash-400 text-sm">
        {t('awakening.completing.body')}
      </p>
    </div>
  );
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
      <p className="max-w-md font-display font-light text-ash-200 text-2xl">
        {t('awakening.errorScreen.heading')}
      </p>
      <p className="mt-4 max-w-md font-body text-ember-400 text-sm" role="alert">
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-10 border border-ash-700 px-10 py-3 font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:border-moonlit-400 hover:text-ash-100"
      >
        {t('awakening.errorScreen.retry')}
      </button>
    </div>
  );
}
